/**
 * apps/web :: pass :: screens/CoursewareCourse
 *
 * Light-courseware course overview for the active country pack
 * (ROADS-569 W1.C5). Reads the traveler's first active `tourist_out`
 * country (fallback "IT"), resolves its pack, and renders the course
 * title, a short progress line, and the module list. Each module row
 * links into the lesson screen (`../learn/<lessonId>`) and shows its
 * index, title, and estimated minutes.
 *
 * Wrapped in PassLayout for consistent pass chrome.
 */
import { Link } from "react-router-dom";

import PassLayout from "../PassLayout";
import {
  getPack,
  useActivePassCountries,
  type PackId,
} from "@/lib/pass-content";

export default function CoursewareCourse(): JSX.Element {
  const country = useActivePassCountries()[0] ?? "IT";
  const pack = getPack(country as PackId);
  const { course } = pack;
  const lessonCount = course.modules.length;
  const totalMinutes = course.modules.reduce((sum, l) => sum + l.minutes, 0);

  return (
    <PassLayout country={country} title={course.title}>
      <p
        className="text-sm text-neutral-700"
        data-testid="courseware-progress"
      >
        {lessonCount} lessons · about {totalMinutes} min total
      </p>

      <ul className="mt-lg flex flex-col gap-2">
        {course.modules.map((lesson, index) => (
          <li key={lesson.id}>
            <Link
              to={`../learn/${lesson.id}`}
              data-testid={`courseware-lesson-${lesson.id}`}
              className="flex items-center gap-3 rounded-md border border-neutral-200 px-md py-sm hover:border-indigo-300 hover:bg-indigo-50"
            >
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-neutral-900">
                  {lesson.title}
                </span>
                <span className="block text-xs text-neutral-500">
                  {lesson.minutes} min
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </PassLayout>
  );
}
